import React from 'react';
import dynamic from 'next/dynamic';
import { BreadcrumbsV2 } from '@zola/zola-ui/src/components/BreadcrumbsV2';
import _flatmap from 'lodash/flatMap';
import { InView } from 'react-intersection-observer';

import { ChecklistPageType, ExpertAdviceFooterType } from 'libs/contentful/types';
import { UserContext } from '@zola-helpers/client/dist/es/@types';
import type { WModuleGroupView } from '@zola/svc-web-api-ts-client';

import { TRACKING_SECTIONS_EA } from 'constants/tracking';
import { PrimaryNavIdentifier } from '@zola-helpers/client/dist/es/constants/navConstants';
import { useIsPostAuthNav3MobileOrDesktop } from 'hooks/useIsPostAuthNav3Active';

import PreauthFooterV3 from '@zola/zola-ui/src/components/PreAuthV3/PreauthFooterV3';
import reviews from 'components/expert-advice/ChecklistPage/reviews';
import ChecklistPageHeader from 'components/expert-advice/ChecklistPageHeader';
import ExpertAdviceFooter from 'components/expert-advice/ExpertAdviceFooter';
import { SectionContainer, HeroSection } from 'components/expert-advice';
import Nav from 'components/Nav';
import DownloadPdfSection from 'components/expert-advice/DownloadPdfSection';

import { PageWrapper } from 'components/LP/v3/PageWrapper.styles';
import {
  BreadcrumbsContainer,
  PostBody,
  StyledFeaturedSection,
  StyledNextUpSection,
} from 'styles/expertAdviceShared.styles';
import { StyledDiv } from 'styles/pages/ChecklistPage.styles';

const TldrSection = dynamic(import('components/expert-advice/TldrSection'));
const ChecklistRangeSection = dynamic(import('components/expert-advice/ChecklistRangeSection'));
const MarketingModuleSection = dynamic(import('components/expert-advice/MarketingModuleSection'));
const TestimonialCarousel = dynamic(import('components/ui/TestimonialCarousel'));
const Faqs = dynamic(import('components/expert-advice/Faqs'));

export type ChecklistArticleProps = {
  expertAdviceFooter: ExpertAdviceFooterType;
  navHTML: string | null;
  userCtx: UserContext | null;
  slug: string;
  page: ChecklistPageType;
  marketingModuleMap?: Record<string, WModuleGroupView>;
};

const ChecklistPage: React.FC<ChecklistArticleProps> = ({
  expertAdviceFooter,
  navHTML,
  userCtx,
  slug,
  page,
  marketingModuleMap,
}) => {
  const {
    topic,
    subtopic,
    faqs,
    title,
    excerpt,
    author,
    heroImage,
    heroCaption,
    heroCredit,
    heroCreditUrl,
    tldr,
    nextUp,
    featured,
    checklistTemplates,
    publishedAt,
    widget1,
  } = page;

  const breadcrumbs = [{ to: '/expert-advice', label: 'Expert advice' }];
  if (topic && topic.name) {
    breadcrumbs.push({ to: `/expert-advice/topic/${topic.slug}`, label: topic.name });
  }
  if (topic && subtopic && subtopic.name) {
    breadcrumbs.push({
      to: `/expert-advice/topic/${topic.slug}/${subtopic.slug}`,
      label: subtopic.name,
    });
  }

  const isPostAuthNav3 = useIsPostAuthNav3MobileOrDesktop(userCtx);

  const ranges = _flatmap(checklistTemplates, t => t.checklistRanges);

  const widget1Tag = widget1?.marketingModule?.tag;
  const widget1View = marketingModuleMap && widget1Tag ? marketingModuleMap[widget1Tag] : undefined;
  const widget1BusinessUnit = widget1?.marketingModule?.businessUnit;
  const numOfRangeMarketingModules = ranges.filter(r => r.widget?.marketingModule?.tag).length;
  const marketingModuleTrackingData =
    marketingModuleMap && widget1Tag
      ? {
          business_unit: widget1BusinessUnit || '',
          section: TRACKING_SECTIONS_EA.CHECKLIST_ARTICLE,
          position: numOfRangeMarketingModules + 1, // appears at the bottom of page
        }
      : undefined;

  return (
    <>
      <ChecklistPageHeader
        breadcrumbs={breadcrumbs}
        ranges={ranges}
        heroCaption={heroCaption}
        title={title}
        heroImage={heroImage}
        publishedAt={publishedAt}
        author={author}
        subtopic={subtopic}
        topic={topic}
        excerpt={excerpt}
        faqs={faqs}
        slug={slug}
      />
      <Nav
        activePrimaryLink={PrimaryNavIdentifier.ADVICE}
        navHTML={navHTML}
        showPromo
        user={userCtx}
        disableSecondary
        disablePrimaryNavCollapse
      />
      <PageWrapper isPostAuthNav3={isPostAuthNav3}>
        <PostBody>
          <BreadcrumbsContainer>
            <BreadcrumbsV2 currentSelectionLabel={title} linkItems={breadcrumbs} />
          </BreadcrumbsContainer>
          <SectionContainer>
            <HeroSection
              title={title}
              excerpt={excerpt}
              author={author}
              heroImage={heroImage}
              heroCredit={heroCredit}
              heroCreditUrl={heroCreditUrl}
              tldr={tldr}
            />
          </SectionContainer>
          <SectionContainer>
            <TldrSection tldr={tldr} />
          </SectionContainer>
          <InView rootMargin="40px 0px 0px 0px" triggerOnce>
            {({ inView, ref }) => (
              <StyledDiv ref={ref}>
                <SectionContainer>
                  <ChecklistRangeSection ranges={ranges} marketingModuleMap={marketingModuleMap} />
                </SectionContainer>
                <DownloadPdfSection inView={inView} {...page} />
              </StyledDiv>
            )}
          </InView>
          <SectionContainer>
            <MarketingModuleSection
              widget={widget1View}
              trackingData={marketingModuleTrackingData}
            />
          </SectionContainer>
          <SectionContainer>
            <StyledNextUpSection nextUp={nextUp} />
            <StyledFeaturedSection featured={featured} />
          </SectionContainer>
          <TestimonialCarousel title={<>Hear from Real Zola Couples</>} testimonials={reviews} />
          <Faqs faqs={faqs} />
        </PostBody>
        <BreadcrumbsContainer borderTop onlyMobile>
          <BreadcrumbsV2 currentSelectionLabel={title} disableBackLink linkItems={breadcrumbs} />
        </BreadcrumbsContainer>
        <ExpertAdviceFooter expertAdviceFooter={expertAdviceFooter} />
        <PreauthFooterV3 isGuest={userCtx ? Boolean(userCtx.is_guest) : true} />
      </PageWrapper>
    </>
  );
};
export default ChecklistPage;
